import React, { useState, useEffect, useRef, useCallback } from "react";
import './styles/App.css';
import {
    bibleBooks,
    WIDTH_OFFSET,
    DEFAULT_MAX_COLUMNS,
    DEFAULT_VERSE_THRESHOLD,
    DEFAULT_MAX_VERSES,
    DEFAULT_MAX_FONT_SIZE,
    getSanitizedBookName,
    bookColorGroupsSequence,
    bookColorGroupsSections,
    paletteColors,
    BookProps,
    SearchResult,
    PaletteColors,
    TOTAL_CELLS
} from './config/constants';
import Header from './components/Header';
import SearchOverlay from './components/SearchOverlay';
import SettingsOverlay from './components/SettingsOverlay';

// Define a type for the Bible data
// Support both KJV (verses array) and ERV (content string) chapter formats
type Chapter =
  | {
      chapter: number;
      verses: {
        verse: number;
        text: string;
      }[];
      content?: never;
    }
  | {
      chapter: number;
      content: string;
      verses?: never;
    };

interface BibleData {
  books: {
    name: string;
    chapters: Chapter[];
  }[];
}

const App: React.FC = () => {
    // Add state to store Bible data fetched at runtime
    const [ervData, setErvData] = useState<BibleData>({ books: [] });
    const [kjvData, setKjvData] = useState<BibleData>({ books: [] });


    // Fetch KJV.json and ERV.json when the component mounts
    useEffect(() => {
        fetch('/KJV.json')
            .then(response => response.json())
            .then(data => setKjvData(data))
            .catch(error => console.error('Error loading KJV Bible data:', error));

        fetch('/ERV.json')
            .then(response => response.json())
            .then(data => setErvData(data))
            .catch(error => console.error('Error loading ERV Bible data:', error));
    }, []);

    const [displaySize, setDisplaySize] = useState({
        rows: 5, // Number of rows visible at a time
        columns: DEFAULT_MAX_COLUMNS, // Start with the maximum number of columns
        height: window.innerHeight-100,
        width: window.innerWidth - WIDTH_OFFSET,
        zoomFactor: DEFAULT_MAX_COLUMNS, // Initial zoom factor
        chapterZoom: DEFAULT_MAX_COLUMNS, // Initial chapter zoom
        verseFactor: 0 // Initial verse factor
    });

    const [zoomIndex, setZoomIndex] = useState(DEFAULT_MAX_COLUMNS + DEFAULT_MAX_VERSES); // Initialize zoomIndex to MAX_COLUMNS + MAX_VERSES
    const containerRef = useRef<HTMLDivElement | null>(null); // Create a ref for the container
    const [currentBookTitle, setCurrentBookTitle] = useState<string>("ELBIBLE"); // Initialize to 'ELBIBLE' instead of null
    const [bookmarks, setBookmarks] = useState<(null | { book: string; chapter: number })[]>([null, null, null, null]);
    const [translation, setTranslation] = useState<'ERV' | 'KJV'>('ERV'); // State for translation
    const [showSettings, setShowSettings] = useState(false); // State for showing settings menu
    const [popEffectBookmarkIndex, setPopEffectBookmarkIndex] = useState<number | null>(null); // State for pop effect
    const [selectedPalette, setSelectedPalette] = useState<string>('palette1');
    const [bookGroupType, setBookGroupType] = useState<'sequence' | 'sections'>('sequence');
    const [generatedColorMappings, setGeneratedColorMappings] = useState<Record<string, Record<string, string>>>({});
    const [bookTitleCaps, setBookTitleCaps] = useState<boolean>(false); // State for book title casing
    const [bookBorderStyle, setBookBorderStyle] = useState<'curved' | 'edged' | 'none'>('curved'); // State for book border style
    const [activeSettingsPage, setActiveSettingsPage] = useState<'cosmetic' | 'function'>('cosmetic');
    const [maxColumns, setMaxColumns] = useState(DEFAULT_MAX_COLUMNS);
    const [verseThreshold, setVerseThreshold] = useState(DEFAULT_VERSE_THRESHOLD);
    const [maxVerses, setMaxVerses] = useState(DEFAULT_MAX_VERSES);
    const [maxFontSize, setMaxFontSize] = useState(DEFAULT_MAX_FONT_SIZE);

    // Refs for bookmark long-press logic
    const bookmarkTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isLongPressActionTriggeredRef = useRef<boolean>(false);

    // State and Refs for the "Down Button"
    const [showDownButton, setShowDownButton] = useState(false);
    const nextChapterWithVersesRef = useRef<HTMLElement | null>(null);
    const downButtonRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        const newGeneratedColorMappings: Record<string, Record<string, string>> = {};
        const activeBookColorGroups = bookGroupType === 'sections' ? bookColorGroupsSections : bookColorGroupsSequence;

        Object.keys(paletteColors).forEach(paletteId => {
            newGeneratedColorMappings[paletteId] = {};
            const currentPaletteDetails = paletteColors[paletteId as keyof typeof paletteColors];
            Object.keys(activeBookColorGroups).forEach((groupKey, index) => {
                const color = currentPaletteDetails.bookCellColors[index % currentPaletteDetails.bookCellColors.length];
                (activeBookColorGroups[groupKey as keyof typeof activeBookColorGroups] as string[]).forEach(bookName => {
                    newGeneratedColorMappings[paletteId][bookName] = color;
                });
            });
        });
        setGeneratedColorMappings(newGeneratedColorMappings);
    }, [bookGroupType]); // Regenerate when bookGroupType changes

    useEffect(() => {
        const currentPaletteDetails = paletteColors[selectedPalette as keyof typeof paletteColors];
        if (currentPaletteDetails) {
            document.documentElement.style.setProperty('--book-cell-text-color', currentPaletteDetails.bookCellText);
            document.documentElement.style.setProperty('--page-color', currentPaletteDetails.page);
            document.documentElement.style.setProperty('--marker-color', currentPaletteDetails.marker);
            document.documentElement.style.setProperty('--pencil-color', currentPaletteDetails.pencil);
            document.documentElement.style.setProperty('--overlay-bg-color', currentPaletteDetails.overlayBg);
            document.documentElement.style.setProperty('--target-outline-color', currentPaletteDetails.targetOutlineColor);
        }

        // Determine border styles based on bookBorderStyle and selectedPalette
        let startBorderRadius = '0px';
        let startBoxShadow = 'none';
        let endBorderRadius = '0px';
        let endBoxShadow = 'none';
        // No longer need --book-corner-radius-val for clip-path

        if (bookBorderStyle === 'edged') {
            endBoxShadow = 'inset -1px 0 0 var(--page-color)';
            // Hacker palette specific style for 'edged'
            if (selectedPalette === 'paletteHacker') {
                startBorderRadius = '0px'; // Hacker specific for edged
                startBoxShadow = 'inset 1px 1px 0 var(--marker-color)';
            }
        } else if (bookBorderStyle === 'curved') {
            startBorderRadius = '6px 0 0 0'; // Applied to the inner .start-of-first div
            endBorderRadius = '0 0 6px 0';
            // startBoxShadow remains 'none' for curved to keep it clean
        }
        // If bookBorderStyle === 'none', the defaults (0px, none) are used.

        document.documentElement.style.setProperty('--book-start-border-radius', startBorderRadius);
        document.documentElement.style.setProperty('--book-start-box-shadow', startBoxShadow);
        document.documentElement.style.setProperty('--book-end-border-radius', endBorderRadius);
        document.documentElement.style.setProperty('--book-end-box-shadow', endBoxShadow);
        // Removed setting --book-corner-radius-val

    }, [selectedPalette, bookBorderStyle]);


    const [showSearchOverlay, setShowSearchOverlay] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const searchOverlayRef = useRef<HTMLDivElement | null>(null);
    const settingsOverlayRef = useRef<HTMLDivElement | null>(null);
    const searchIconRef = useRef<HTMLButtonElement | null>(null);
    const settingsIconRef = useRef<HTMLButtonElement | null>(null);


    const handleTranslationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedTranslation = event.target.value as 'ERV' | 'KJV';
        setTranslation(selectedTranslation);
        localStorage.setItem('preferredTranslation', selectedTranslation); // Save to localStorage

        if (zoomIndex <= DEFAULT_MAX_VERSES) {
            const container = containerRef.current;
            if (container) {
                // Set the chapter above the one at 91 pixels as the target cell
                const cells = container.querySelectorAll('.grid-item');
                let targetCell: HTMLElement | null = null;

                cells.forEach((cell) => {
                    const rect = cell.getBoundingClientRect();
                    if (rect.top <= 91 && rect.bottom > 91) {
                        const bookTitle = cell.getAttribute('data-book');
                        let chapterNumber = parseInt(cell.getAttribute('data-chapter') || "0", 10);

                        // Adjust to the chapter above, ensuring it doesn't go below 0
                        chapterNumber = Math.max(0, chapterNumber - 1);

                        targetCell = container.querySelector(
                            `.grid-item[data-book="${bookTitle}"][data-chapter="${chapterNumber}"]`
                        ) as HTMLElement | null;
                    }

                    // Reset the contents of the cell
                    const verseContainer = cell.querySelector('.verse-container');
                    if (verseContainer) {
                        verseContainer.remove(); // Remove the verse container
                    }

                    cell.classList.remove('visible'); // Clear visibility from all chapters
                });

                if (targetCell) {
                    (targetCell as HTMLElement).classList.add('target'); // Mark the target cell
                    requestAnimationFrame(() => {
                        (targetCell as HTMLElement).scrollIntoView({ block: 'start' }); // Scroll to the target cell
                    });
                }
            }
        }
    };

    const currentData = translation === 'ERV' ? ervData : kjvData; // Use the fetched data

    const checkAndShowDownButton = useCallback((currentChapterElement: HTMLElement) => {
        if (showDownButton || zoomIndex >= maxVerses) { // If button is already shown or zoom is too high, do nothing
            return;
        }

        let nextChapterCandidate = currentChapterElement.nextElementSibling as HTMLElement | null;
        let nextChapterElement: HTMLElement | null = null;

        while (nextChapterCandidate) {
            if (nextChapterCandidate.classList.contains('grid-item') && 
                nextChapterCandidate.getAttribute('data-chapter') && 
                nextChapterCandidate.getAttribute('data-chapter') !== "0") {
                nextChapterElement = nextChapterCandidate;
                break;
            }
            nextChapterCandidate = nextChapterCandidate.nextElementSibling as HTMLElement | null;
        }

        if (nextChapterElement) {
            const nextBookTitle = nextChapterElement.getAttribute('data-book');
            const nextChapterNumberStr = nextChapterElement.getAttribute('data-chapter');

            if (nextBookTitle && nextChapterNumberStr) {
                const nextChapterNumberVal = parseInt(nextChapterNumberStr, 10);
                const bookData = currentData.books.find(book => book.name === nextBookTitle);
                const chapterData = bookData?.chapters.find(chapter => chapter.chapter === nextChapterNumberVal);

                if (chapterData?.verses && chapterData.verses.length > 0) {
                    // Next chapter has verses
                    nextChapterWithVersesRef.current = nextChapterElement;
                    setShowDownButton(true);
                }
            }
        }
    }, [showDownButton, currentData, zoomIndex]); // Added zoomIndex to dependencies


    const handleSearchIconClick = () => {
        setShowSearchOverlay(prev => !prev);
        if (showSearchOverlay) { // If closing search, clear results
            setSearchTerm("");
            setSearchResults([]);
        }
    };

    const performSearch = useCallback(() => {
        if (!searchTerm.trim()) {
            setSearchResults([]);
            return;
        }
        const results: SearchResult[] = [];
        const lowerSearchTerm = searchTerm.toLowerCase();

        currentData.books.forEach(book => {
            book.chapters.forEach(chapter => {
                if (chapter.verses) {
                    chapter.verses.forEach(verse => {
                        if (verse.text.toLowerCase().includes(lowerSearchTerm)) {
                            results.push({
                                book: book.name,
                                chapter: chapter.chapter,
                                verse: verse.verse,
                                text: verse.text
                            });
                        }
                    });
                }
                // Optionally, add ERV content search here if needed
            });
        });
        setSearchResults(results);
    }, [searchTerm, currentData]);

    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (showSearchOverlay && searchTerm.trim()) {
            searchTimeoutRef.current = setTimeout(() => {
                performSearch();
            }, 500); // 500ms delay
        } else if (!searchTerm.trim()) {
            setSearchResults([]);
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm, showSearchOverlay, performSearch]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                searchOverlayRef.current &&
                !searchOverlayRef.current.contains(event.target as Node) &&
                searchIconRef.current &&
                !searchIconRef.current.contains(event.target as Node)
            ) {
                setShowSearchOverlay(false);
                setSearchTerm("");
                setSearchResults([]);
            }
        };

        if (showSearchOverlay) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showSearchOverlay]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                settingsOverlayRef.current &&
                !settingsOverlayRef.current.contains(event.target as Node) &&
                settingsIconRef.current &&
                !settingsIconRef.current.contains(event.target as Node)
            ) {
                setShowSettings(false);
            }
        };

        if (showSettings) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showSettings]);

    const handleSearchResultClick = (result: SearchResult) => {
        const container = containerRef.current;
        if (!container) {
            setShowSearchOverlay(false);
            setSearchTerm("");
            setSearchResults([]);
            return;
        }
    
        setZoomIndex(0); // Zoom to verse level first
    
        requestAnimationFrame(() => {
            const actualBook = result.book;
            const actualChapter = result.chapter;
            let scrollTargetChapter = actualChapter; // Default to the actual chapter
    
            const actualChapterCellSelector = `.grid-item[data-book="${actualBook}"][data-chapter="${actualChapter}"]`;
            const actualChapterCell = container.querySelector(actualChapterCellSelector) as HTMLElement | null;
    
            // Align with bookmark logic: adjust scroll target only if actual cell exists and has no verse container
            if (actualChapterCell && !actualChapterCell.querySelector('.verse-container')) {
                scrollTargetChapter = Math.max(0, actualChapter - 1); // Navigate to 1 chapter above
            }
            // In other cases (actualChapterCell is null, or it exists and has a .verse-container),
            // scrollTargetChapter remains actualChapter.
    
            const finalScrollTargetCellSelector = `.grid-item[data-book="${actualBook}"][data-chapter="${scrollTargetChapter}"]`;
            const finalScrollTargetCell = container.querySelector(finalScrollTargetCellSelector) as HTMLElement | null;
    
            if (finalScrollTargetCell) {
                // Clear previous targets
                container.querySelectorAll('.grid-item.target').forEach(cell => cell.classList.remove('target'));
    
                // Highlight the cell we are scrolling to (aligning with bookmark behavior)
                finalScrollTargetCell.classList.add('target');
                
                finalScrollTargetCell.scrollIntoView({ block: 'start' });
    
                // Ensure visibility and verse content are updated after scrolling
                setTimeout(() => {
                    handleVisibility(); 
                    updateVisibleVerses();
                }, 100); // Delay to allow scroll and DOM updates
            }
    
            setShowSearchOverlay(false);
            setSearchTerm("");
            setSearchResults([]);
        });
    };


    useEffect(() => {
        const updateDisplaySize = () => {
            setDisplaySize((prevSize) => ({
                ...prevSize,
                height: window.innerHeight-60,
                width: window.innerWidth - WIDTH_OFFSET
            }));
        };

        window.addEventListener("resize", updateDisplaySize);
        return () => window.removeEventListener("resize", updateDisplaySize);
    }, []);

    useEffect(() => {
        const savedTranslation = localStorage.getItem('preferredTranslation') as 'ERV' | 'KJV' | null;
        if (savedTranslation) {
            setTranslation(savedTranslation); // Load saved translation on app start
        }

        const savedBookmarks = localStorage.getItem('bookmarks');
        if (savedBookmarks) {
            setBookmarks(JSON.parse(savedBookmarks)); // Load saved bookmarks on app start
        }

        const savedPalette = localStorage.getItem('selectedPalette');
        if (savedPalette && paletteColors[savedPalette as keyof typeof paletteColors]) {
            setSelectedPalette(savedPalette);
        }

        const savedBookGroupType = localStorage.getItem('bookGroupType') as 'sequence' | 'sections' | null;
        if (savedBookGroupType) {
            setBookGroupType(savedBookGroupType);
        }
        const savedBookTitleCaps = localStorage.getItem('bookTitleCaps');
        if (savedBookTitleCaps) {
            setBookTitleCaps(JSON.parse(savedBookTitleCaps));
        }
        const savedBookBorderStyle = localStorage.getItem('bookBorderStyle') as 'curved' | 'edged' | 'none' | null;
        if (savedBookBorderStyle) {
            setBookBorderStyle(savedBookBorderStyle);
        }

        let loadedMaxColumns = DEFAULT_MAX_COLUMNS;
        const savedMaxColumns = localStorage.getItem('maxColumns');
        if (savedMaxColumns) {
            loadedMaxColumns = JSON.parse(savedMaxColumns);
            setMaxColumns(loadedMaxColumns);
        }
        const savedVerseThreshold = localStorage.getItem('verseThreshold');
        if (savedVerseThreshold) {
            setVerseThreshold(JSON.parse(savedVerseThreshold));
        }

        let loadedMaxVerses = DEFAULT_MAX_VERSES;
        const savedMaxVerses = localStorage.getItem('maxVerses');
        if (savedMaxVerses) {
            loadedMaxVerses = JSON.parse(savedMaxVerses);
            setMaxVerses(loadedMaxVerses);
        }
        const savedMaxFontSize = localStorage.getItem('maxFontSize');
        if (savedMaxFontSize) {
            setMaxFontSize(JSON.parse(savedMaxFontSize));
        }

        // Set zoomIndex based on loaded values to ensure it starts maxed out
        setZoomIndex(loadedMaxColumns + loadedMaxVerses);
    }, []);

    const handlePaletteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPalette = event.target.value;
        setSelectedPalette(newPalette);
        localStorage.setItem('selectedPalette', newPalette);
    };

    const handleBookGroupTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newGroupType = event.target.value as 'sequence' | 'sections';
        setBookGroupType(newGroupType);
        localStorage.setItem('bookGroupType', newGroupType);
    };

    const handleBookTitleCapsChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newCapsSetting = event.target.value === 'uppercase';
        setBookTitleCaps(newCapsSetting);
        localStorage.setItem('bookTitleCaps', JSON.stringify(newCapsSetting));
    };

    const handleBookBorderStyleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newBorderStyle = event.target.value as 'curved' | 'edged' | 'none';
        setBookBorderStyle(newBorderStyle);
        localStorage.setItem('bookBorderStyle', newBorderStyle);
    };

    const handleMaxColumnsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newMaxColumns = parseInt(event.target.value, 10);
        setMaxColumns(newMaxColumns);
        localStorage.setItem('maxColumns', JSON.stringify(newMaxColumns));
        // Also adjust verseThreshold if it's now larger than maxColumns
        if (verseThreshold > newMaxColumns) {
            setVerseThreshold(newMaxColumns);
            localStorage.setItem('verseThreshold', JSON.stringify(newMaxColumns));
        }
    };

    const handleVerseThresholdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newVerseThreshold = parseInt(event.target.value, 10);
        setVerseThreshold(newVerseThreshold);
        localStorage.setItem('verseThreshold', JSON.stringify(newVerseThreshold));
    };

    const handleMaxVersesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newMaxVerses = parseInt(event.target.value, 10);
        setMaxVerses(newMaxVerses);
        localStorage.setItem('maxVerses', JSON.stringify(newMaxVerses));
    };

    const handleMaxFontSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newMaxFontSize = parseInt(event.target.value, 10);
        setMaxFontSize(newMaxFontSize);
        localStorage.setItem('maxFontSize', JSON.stringify(newMaxFontSize));
    };

    const updateVisibleVerses = useCallback(() => {
        if (!containerRef.current) {
            return;
        }

        const container = containerRef.current;
        const rows = container.querySelectorAll('div[data-book].visible');

        rows.forEach((row) => {
            const verseContainer = row.querySelector('.verse-container') as HTMLElement;
            if (verseContainer) {
                // Dynamically calculate and set font size
                const currentStep = displaySize.verseFactor;
                const totalSteps = maxVerses;
                // New formula: (maxFontSize / (totalSteps + 1)) * currentStep
                const calculatedFontSize = totalSteps > 0 ? (maxFontSize / (totalSteps + 1)) * currentStep : maxFontSize;
                verseContainer.style.fontSize = `${calculatedFontSize}px`;
            }
        });
    }, [displaySize.verseFactor, maxVerses, maxFontSize]);

    const handleVisibility = useCallback(() => {
        if (!containerRef.current) {
            return;
        }

        const container = containerRef.current;
        const rows = container.querySelectorAll('div[data-book]');

        if (zoomIndex > maxVerses) {
            rows.forEach((row) => {
                row.classList.remove('visible');
                const verseContainer = row.querySelector('.verse-container');
                if (verseContainer) verseContainer.remove();
            });
            return;
        }

        const containerRect = container.getBoundingClientRect();
        rows.forEach((row) => {
            const rect = row.getBoundingClientRect();
            if (rect.top >= containerRect.top + 19 - 1 && rect.bottom <= containerRect.bottom) {
                console.log(`ADDING VISIBLE`);
                row.classList.add('visible');
                const bookTitle = row.getAttribute('data-book');
                const chapterNumber = row.getAttribute('data-chapter');
                if (displaySize.verseFactor > 0 && chapterNumber !== "0" && !row.querySelector('.verse-container')) {
                    const verseDiv = document.createElement('div');
                    verseDiv.className = 'verse-container';

                    // Fetch the actual verses from the current data
                    // Use toLowerCase() for case-insensitive matching of book titles
                    const bookData = currentData.books.find(
                        book => book.name.toLowerCase() === (bookTitle || '').toLowerCase()
                    );
                    const chapterData = bookData?.chapters.find(chapter => chapter.chapter === parseInt(chapterNumber || "0", 10));
                    if (translation === 'ERV' && chapterData?.content) {
                        // Use the HTML-formatted content directly
                        verseDiv.innerHTML = chapterData.content;
                    } else {
                        // Fallback for KJV and other formats
                        const verses = chapterData?.verses || [];
                        const verseContent = verses.map((verseObj) => {
                            return `<span class="verse-number">${verseObj.verse}</span> ${verseObj.text}`;
                        }).join(' ');
                        if (verseContent) {
                            verseDiv.innerHTML = verseContent;
                        } else {
                            verseDiv.innerHTML = `<pre> - </pre>`;
                        }
                    }

                    // Add copyright information
                    const copyrightDiv = document.createElement('div');
                    copyrightDiv.className = 'verse-copyright';
                    if (translation === 'ERV') {
                        copyrightDiv.innerHTML = 'Holy Bible: Easy-to-Read Version (ERV), International Edition © 2013, 2016 Bible League International. Used by permission. All rights reserved.';
                    } else if (translation === 'KJV') {
                        copyrightDiv.innerHTML = 'King James Version (Public Domain)';
                    }
                    verseDiv.appendChild(copyrightDiv);

                    // Add a click event listener to the verse-container to stop propagation
                    verseDiv.addEventListener('click', (e) => e.stopPropagation());
                    row.appendChild(verseDiv);
                    // After adding a verse container, check if we should show the down button
                    checkAndShowDownButton(row as HTMLElement);
                }
            } else {
                const verseContainer = row.querySelector('.verse-container');
                if (verseContainer) {
                }
            }
        });
    }, [zoomIndex, displaySize.verseFactor, currentData, checkAndShowDownButton]); // Added checkAndShowDownButton

    const updateHeaderBookTitle = useCallback(() => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const cells = container.querySelectorAll('.grid-item');
        let newTitleSet = false;

        cells.forEach((cell) => {
            if (newTitleSet) return; // Optimization: once title is set, no need to check other cells in this run

            const rect = cell.getBoundingClientRect();
            if (rect.top <= 61 && rect.bottom > 61 && rect.left <= 0 && rect.right > 0) {
                const bookTitleFromAttr = cell.getAttribute('data-book');
                const chapterNumber = cell.getAttribute('data-chapter');
                const isGenesisStart = cell.classList.contains('genesis') && cell.classList.contains('start-of-first');

                if (isGenesisStart) {
                    if (currentBookTitle !== 'ELBIBLE') {
                        setCurrentBookTitle('ELBIBLE');
                    }
                } else if (bookTitleFromAttr) {
                    const baseTitle = bookTitleCaps ? bookTitleFromAttr.toUpperCase() : bookTitleFromAttr;
                    const titleWithChapter = chapterNumber && chapterNumber !== "0" && zoomIndex <= maxVerses
                        ? `${baseTitle} ${chapterNumber}`
                        : baseTitle;
                    if (titleWithChapter !== currentBookTitle) {
                        setCurrentBookTitle(titleWithChapter);
                    }
                }
                newTitleSet = true; // Mark that title has been determined for this call
            }
        });
    }, [currentBookTitle, zoomIndex, bookTitleCaps]); // Added bookTitleCaps

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            handleVisibility();
            updateVisibleVerses();
            updateHeaderBookTitle(); // Update the header on scroll
        };

        container.addEventListener('scroll', handleScroll);
        // Initial call to set the header title correctly when dependencies change
        updateHeaderBookTitle(); 

        return () => container.removeEventListener('scroll', handleScroll);
    }, [handleVisibility, updateVisibleVerses, updateHeaderBookTitle, bookTitleCaps]); // Added bookTitleCaps

    useEffect(() => {
        handleVisibility();
        updateVisibleVerses();
    }, [updateVisibleVerses, handleVisibility, displaySize.verseFactor]);

    const handleWheel = useCallback((event: WheelEvent) => {
        event.preventDefault();
        const delta = event.deltaY > 0 ? 1 : -1; // Determine zoom direction

        setZoomIndex((prevZoomIndex) => {
            const step = prevZoomIndex > verseThreshold ? 2 : 1; // Move 2 steps if above verseThreshold
            const newZoomIndex = Math.max(0, Math.min(maxColumns + maxVerses, prevZoomIndex + delta * step)); // Clamp zoomIndex
            console.log(`Zoom Index: ${newZoomIndex}`); // Log the updated zoom index

            const container = containerRef.current;
            if (container) {
                const targetCell = container.querySelector('.grid-item.target') as HTMLElement;
                if (targetCell) {
                    requestAnimationFrame(() => {
                        targetCell.scrollIntoView({
                            block: newZoomIndex <= maxVerses ? 'start' : 'center' // Adjust alignment based on zoomIndex
                        });
                    });
                }
            }

            return newZoomIndex;
        });
    }, [maxColumns, maxVerses, verseThreshold]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        container.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, [handleWheel]);

    useEffect(() => {
        const handleMouseMove = (event: MouseEvent) => {
            const container = containerRef.current;
            if (!container) return;

            const allCells = container.querySelectorAll('.grid-item');
            allCells.forEach((cell) => cell.classList.remove('target'));

            const elementUnderMouse = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement;
            if (!elementUnderMouse) return;

            const hoveredCell = elementUnderMouse.closest('.grid-item');
            if (!hoveredCell) return;
            hoveredCell.classList.add('target');

        };

        window.addEventListener('mousemove', handleMouseMove);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, [displaySize.columns]);

    const animationRef = useRef<number | null>(null); // Ref to track animation frame
    const latestTouchDist = useRef<number>(0); // Ref to track the latest distance

    useEffect(() => {
        let touchStartDist = 0;
        let touchStartMidpoint = { x: 0, y: 0 };
        let targetCell: HTMLElement | null = null; // Store the target cell
        let initialZoomIndex = 0; // Save the initial zoom index at touchstart

        const animate = () => {
            const maxDist = 1.25 * window.innerHeight - 60; // Maximum distance for scaling
            const distChange = latestTouchDist.current - touchStartDist;
            const ratio = distChange / maxDist;

            const deltaZoom = Math.floor(ratio * maxColumns); // Calculate zoom change based on ratio
            const step = initialZoomIndex > verseThreshold ? 2 : 1; // Move 2 steps if above verseThreshold
            const newZoomIndex = Math.max(0, Math.min(maxColumns + maxVerses, initialZoomIndex - deltaZoom * step)); // Clamp

            console.log(`Zoom Index: ${newZoomIndex}`); // Log the new zoom index

            setZoomIndex(newZoomIndex); // Update zoomIndex directly
            animationRef.current = requestAnimationFrame(animate);
        };

        const handleTouchStart = (event: TouchEvent) => {
            if (event.touches.length === 2) {
                event.preventDefault(); // Stop browser zooming

                const touch1 = event.touches[0];
                const touch2 = event.touches[1];

                touchStartMidpoint = {
                    x: (touch1.clientX + touch2.clientX) / 2,
                    y: (touch1.clientY + touch2.clientY) / 2,
                };

                touchStartDist = Math.hypot(
                    touch1.clientX - touch2.clientX,
                    touch1.clientY - touch2.clientY
                );
                latestTouchDist.current = touchStartDist; // Initialize the latest distance
                initialZoomIndex = zoomIndex; // Use the current zoomIndex

                // Set the chapter located at the touchStartMidpoint as the targetCell
                const elementAtMidpoint = document.elementFromPoint(touchStartMidpoint.x, touchStartMidpoint.y) as HTMLElement | null;
                if (elementAtMidpoint) {
                    const midpointCell = elementAtMidpoint.closest('.grid-item') as HTMLElement | null;

                    if (midpointCell) {
                        const container = containerRef.current;
                        if (container) {
                            const allCells = container.querySelectorAll('.grid-item');
                            allCells.forEach((cell) => cell.classList.remove('target')); // Remove existing targets
                        }
                        midpointCell.classList.add('target'); // Set the midpoint cell as the target
                        targetCell = midpointCell; // Store the target cell
                    }
                }

                // Start the animation loop
                if (!animationRef.current) {
                    animationRef.current = requestAnimationFrame(animate);
                }
            }
        };

        const handleTouchMove = (event: TouchEvent) => {
            if (event.touches.length === 2) {
                event.preventDefault();

                const touch1 = event.touches[0];
                const touch2 = event.touches[1];
                latestTouchDist.current = Math.hypot(
                    touch1.clientX - touch2.clientX,
                    touch1.clientY - touch2.clientY
                ); // Update the latest distance
            }
        };

        const handleTouchEnd = () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }
            touchStartDist = 0;
            targetCell = null;
        };

        window.addEventListener("touchstart", handleTouchStart, { passive: false });
        window.addEventListener("touchmove", handleTouchMove, { passive: false });
        window.addEventListener("touchend", handleTouchEnd);

        return () => {
            window.removeEventListener("touchstart", handleTouchStart);
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("touchend", handleTouchEnd);
        };
    }, [zoomIndex, containerRef, maxColumns, maxVerses, verseThreshold]);

    // Effect to handle column changes and scroll adjustments when zoomIndex changes
    useEffect(() => {
        const handleZoomIndexChange = () => {
            setDisplaySize((prevSize) => {
                const newDisplaySize = { ...prevSize };

                if (zoomIndex <= maxVerses) {
                    newDisplaySize.verseFactor = maxVerses - zoomIndex + 1;
                    newDisplaySize.columns = 1;
                } else {
                    newDisplaySize.verseFactor = 0;
                    newDisplaySize.rows = Math.max(1, maxColumns - (zoomIndex - maxVerses));
                    newDisplaySize.columns = Math.min(maxColumns, zoomIndex - maxVerses);
                }

                const container = containerRef.current;
                if (container) {
                    const targetCell = container.querySelector('.grid-item.target') as HTMLElement;
                    if (targetCell) {
                        requestAnimationFrame(() => {
                            targetCell.scrollIntoView({ block: zoomIndex <= maxVerses ? 'start' : 'center' }); // Adjust alignment based on zoomIndex
                        });
                    }
                }

                // Dynamically update book title font size
                const bookTitles = document.querySelectorAll('.book-title');
                bookTitles.forEach((title) => {
                    (title as HTMLElement).style.fontSize = zoomIndex <= maxVerses ? '30px' : '16px';
                });

                return newDisplaySize;
            });
        };

        handleZoomIndexChange();

        // If zoomIndex goes above or equals maxVerses, hide the down button
        if (zoomIndex > maxVerses) {
            setShowDownButton(false);
            nextChapterWithVersesRef.current = null;
        }
    }, [zoomIndex, maxColumns, maxVerses]); // Run whenever zoomIndex changes

    // Effect for handling clicks outside the down button to close it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (showDownButton && downButtonRef.current && !downButtonRef.current.contains(event.target as Node)) {
                setShowDownButton(false);
                nextChapterWithVersesRef.current = null;
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside); // Add touchstart listener
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside); // Clean up touchstart listener
        };
    }, [showDownButton]);


    const rowHeight = displaySize.verseFactor >= 1 ? 'auto' : `${Math.max(displaySize.height / Math.ceil((TOTAL_CELLS / maxColumns)))}px`; // Set row height to auto when verseFactor is 1 or more

    const columnWidth = `${(displaySize.width+3) / displaySize.columns}px`; // Width of each column based on the number of columns

    const handleBookTitleClick = (bookTitle: string) => {
        setZoomIndex((prevZoomIndex) => {
                return (maxVerses + verseThreshold); // Zoom in to show verses
        });
    };

    const handleChapterClick = (bookTitle: string, chapterNumber: number) => {
        console.log(`Chapter clicked: ${bookTitle}, Chapter ${chapterNumber}`); // Debug
        setZoomIndex((prevZoomIndex) => {
            if (prevZoomIndex >= maxVerses) {
                const targetChapter = Math.max(0, chapterNumber - 1); // Navigate to 1 chapter above
                const container = containerRef.current;
                if (container) {
                    const targetCell = container.querySelector(`.grid-item[data-book="${bookTitle}"][data-chapter="${targetChapter}"]`) as HTMLElement;
                    if (targetCell) {
                        container.querySelectorAll('.grid-item').forEach((cell) => cell.classList.remove('target'));
                        targetCell.classList.add('target'); // Mark as target cell
                        requestAnimationFrame(() => {
                            targetCell.scrollIntoView({ block: 'start' }); // Scroll to the target cell
                        });
                    }
                }
                return 0; // Zoom in to show the chapter's verses
            } else if (prevZoomIndex === 0) {
                return maxVerses + verseThreshold; // Zoom out to show chapters
            }
            return prevZoomIndex; // No change
        });
    };

    const handleBookmarkClick = (index: number) => {
        if (isLongPressActionTriggeredRef.current) {
            // If a long press action (reset) was triggered for this interaction,
            // do not proceed with the normal click action (navigation/setting).
            // The flag is reset on the next mousedown/touchstart.
            return;
        }

        const container = containerRef.current;
        if (!container) return;

        if (!bookmarks[index] && zoomIndex <= maxVerses) {
            // Save the bookmark
            const cells = container.querySelectorAll('.grid-item');
            cells.forEach((cell) => {
                const rect = cell.getBoundingClientRect();
                if (rect.top <= 106 && rect.bottom > 106) { // Check if the cell is 106px from the top
                    const book = cell.getAttribute('data-book');
                    const chapter = parseInt(cell.getAttribute('data-chapter') || "0", 10);
                    if (book && chapter !== 0) {
                        const newBookmarks = [...bookmarks];
                        newBookmarks[index] = { book, chapter };
                        setBookmarks(newBookmarks);
                        localStorage.setItem('bookmarks', JSON.stringify(newBookmarks)); // Save bookmarks to localStorage
                    }
                }
            });
        } else if (bookmarks[index] !== null) {
            // Navigate to the bookmarked location
            const { book, chapter } = bookmarks[index]!;
            let targetChapter = chapter; // Default to the bookmarked chapter

            const bookmarkedCell = container.querySelector(`.grid-item[data-book="${book}"][data-chapter="${chapter}"]`) as HTMLElement;

            // Check if the verse-container div exists within the bookmarked cell
            if (bookmarkedCell && !bookmarkedCell.querySelector('.verse-container')) {
                targetChapter = Math.max(0, chapter - 1); // Navigate to 1 chapter above if verse container doesn't exist
            } else if (bookmarkedCell && bookmarkedCell.querySelector('.verse-container')) {
                // If verse container exists, target the actual bookmarked chapter.
                // No change needed for targetChapter, it's already 'chapter'.
            }
            
            setZoomIndex(0); // Set zoomIndex to 0
            const targetCellSelector = `.grid-item[data-book="${book}"][data-chapter="${targetChapter}"]`;
            const targetCell = container.querySelector(targetCellSelector) as HTMLElement;

            if (targetCell) {
                container.querySelectorAll('.grid-item').forEach((cell) => cell.classList.remove('target'));
                targetCell.classList.add('target'); // Mark as target cell
                requestAnimationFrame(() => {
                    targetCell.scrollIntoView({ block: 'start' }); // Scroll to the target cell
                });
            }
        }
    };

    const handleBookmarkReset = (index: number) => {
        const newBookmarks = [...bookmarks];
        newBookmarks[index] = null; // Reset the bookmark
        setBookmarks(newBookmarks);
        localStorage.setItem('bookmarks', JSON.stringify(newBookmarks)); // Save updated bookmarks to localStorage

        setPopEffectBookmarkIndex(index);
        setTimeout(() => {
            setPopEffectBookmarkIndex(null);
        }, 300); // Duration matches CSS animation
    };

    const handleMouseDown = (index: number) => {
        isLongPressActionTriggeredRef.current = false; // Reset flag for new press sequence
        if (bookmarkTimeoutRef.current) {
            clearTimeout(bookmarkTimeoutRef.current); // Clear any existing timeout
        }
        bookmarkTimeoutRef.current = setTimeout(() => {
            isLongPressActionTriggeredRef.current = true; // Mark that long press action has occurred
            handleBookmarkReset(index);
        }, 1000);
    };

    const handleMouseUp = () => {
        if (bookmarkTimeoutRef.current) {
            clearTimeout(bookmarkTimeoutRef.current);
            bookmarkTimeoutRef.current = null;
        }
        // isLongPressActionTriggeredRef.current remains as set by the timeout or mousedown
        // It will be reset by the next mousedown/touchstart
    };

    const handleTouchStart = (index: number) => { // This is for bookmarks
        isLongPressActionTriggeredRef.current = false; // Reset flag for new press sequence
        if (bookmarkTimeoutRef.current) {
            clearTimeout(bookmarkTimeoutRef.current); // Clear any existing timeout
        }
        bookmarkTimeoutRef.current = setTimeout(() => {
            isLongPressActionTriggeredRef.current = true; // Mark that long press action has occurred
            handleBookmarkReset(index);
        }, 1000);
    };

    const handleTouchEnd = () => { // This is for bookmarks
        if (bookmarkTimeoutRef.current) {
            clearTimeout(bookmarkTimeoutRef.current);
            bookmarkTimeoutRef.current = null;
        }
        // isLongPressActionTriggeredRef.current remains as set by the timeout or touchstart
        // It will be reset by the next mousedown/touchstart
    };

    let booksRendered: React.ReactElement<BookProps>[] = [];

    let cellNumber = 1; // Start numbering cells from 1
    for (let i = 0; i < bibleBooks.length; i++) {
        const book = bibleBooks[i];
        const sanitizedBookName = getSanitizedBookName(book.title);
        const currentBookBackgroundColor = generatedColorMappings[selectedPalette]?.[sanitizedBookName] || '#CCCCCC';
        const displayBookTitle = bookTitleCaps ? book.title.toUpperCase() : book.title;

        const isCurvedNonFirst = bookBorderStyle === 'curved' && i > 0;
        
        if (isCurvedNonFirst) {
            const previousBook = bibleBooks[i-1];
            const previousBookSanitizedName = getSanitizedBookName(previousBook.title);
            const previousBookBackgroundColorForCorner = generatedColorMappings[selectedPalette]?.[previousBookSanitizedName] || '#CCCCCC';

            // Outer div (wrapper) is the grid item.
            // It gets the z-index of the CURRENT book's start-of-first cell.
            // Its background is the PREVIOUS book's color.
            // It should not have border-radius or box-shadow itself.
            booksRendered.push(
                <div
                    key={`${book.title}-title-wrapper`}
                    // Add current book's sanitized name and 'start-of-first' for z-index.
                    className={`grid-item ${sanitizedBookName} start-of-first previous-book-corner-wrapper`}
                    style={{ 
                        gridColumn: `span 1`, 
                        gridRow: `span 1`,
                        backgroundColor: previousBookBackgroundColorForCorner,
                        // Override border-radius and box-shadow from the .start-of-first class for the wrapper itself.
                        borderRadius: '0px',
                        boxShadow: 'none',
                    }}
                    data-book={book.title} // Data attributes still refer to the current book
                    data-chapter={0}
                    data-cell-number={cellNumber++}
                    onClick={() => handleBookTitleClick(book.title)}
                >
                    {/* Inner div: current book's color, actual radius/shadow, and content */}
                    <div 
                        style={{ 
                            width: '100%', 
                            height: '100%',
                            display: 'flex', 
                            flexDirection: 'column', 
                            justifyContent: 'center',
                            textAlign: 'center',
                            backgroundColor: currentBookBackgroundColor,
                            borderRadius: `var(--book-start-border-radius, 0px)`, // Applied here
                            boxShadow: `var(--book-start-box-shadow, none)`,      // Applied here
                            position: 'relative', 
                        }}
                    >
                        <div className="book-title" style={{ pointerEvents: 'none'}}>{displayBookTitle}</div>
                    </div>
                </div>
            );
        } else {
            // Standard rendering: .start-of-first is part of the .grid-item
            // This handles "No Border", "Edged Border", and Genesis for "Curved Border"
            // The .start-of-first class (with overflow:hidden) will apply radius/shadow correctly.
            booksRendered.push(
                <div
                    key={`${book.title}-title`}
                    className={`grid-item ${sanitizedBookName} start-of-first`}
                    style={{ 
                        gridColumn: `span 1`, 
                        gridRow: `span 1`,
                        backgroundColor: currentBookBackgroundColor,
                    }}
                    data-book={book.title}
                    data-chapter={0}
                    data-cell-number={cellNumber++}
                    onClick={() => handleBookTitleClick(book.title)}
                >
                    <div className="book-title" style={{ pointerEvents: 'none'}}>{displayBookTitle}</div>
                </div>
            );
        }

        // Render the chapters
        for (let chapter = 1; chapter <= book.chapters; chapter++) {
            const isEndChapter = chapter === book.chapters;
            // Condition for the special "swapped color" corner effect
            const showSwappedColorCornerEffect = 
                bookBorderStyle === 'curved' && 
                isEndChapter && 
                i < bibleBooks.length - 1 && // Not the last book (Revelation)
                zoomIndex > maxVerses;     // Only when zoomed out beyond verse level

            if (showSwappedColorCornerEffect) {
                const nextBook = bibleBooks[i + 1];
                const nextBookSanitizedName = getSanitizedBookName(nextBook.title);
                const nextBookBackgroundColor = generatedColorMappings[selectedPalette]?.[nextBookSanitizedName] || '#CCCCCC';

                // Outer div (wrapper) is the grid item.
                // Its background is the NEXT book's color.
                booksRendered.push(
                    <div
                        key={`${book.title}-chapter-${chapter}-wrapper`}
                        className={`grid-item ${sanitizedBookName} end-of-last next-book-corner-wrapper`}
                        style={{
                            gridColumn: `span 1`,
                            gridRow: `span 1`,
                            backgroundColor: nextBookBackgroundColor, // NEXT book's color
                            borderRadius: '0px', // Override .end-of-last styles for the wrapper
                            boxShadow: 'none',   // Override .end-of-last styles for the wrapper
                            position: 'relative', 
                        }}
                        data-book={book.title}
                        data-chapter={chapter}
                        data-cell-number={cellNumber++}
                        onClick={() => handleChapterClick(book.title, chapter)}
                    >
                        {/* Inner div: CURRENT book's color, actual radius/shadow, and chapter number */}
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                backgroundColor: currentBookBackgroundColor, // CURRENT book's color
                                borderRadius: `var(--book-end-border-radius, 0px)`, // Applied here
                                boxShadow: `var(--book-end-box-shadow, none)`,      // Applied here
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center', // Vertically center chapter number
                                position: 'relative', 
                            }}
                        >
                            {displaySize.columns <= verseThreshold && (
                                <div className="chapter-number">{chapter}</div>
                            )}
                        </div>
                    </div>
                );
            } else {
                // Standard rendering for chapters.
                // If bookBorderStyle === 'curved', zoomIndex <= MAX_VERSES, and isEndChapter,
                // the .end-of-last class will apply the curve to this cell (currentBookBackgroundColor).
                booksRendered.push(
                    <div
                        key={`${book.title}-chapter-${chapter}`}
                        className={`grid-item ${sanitizedBookName} ${isEndChapter ? 'end-of-last' : ''}`}
                        style={{ 
                            gridColumn: `span 1`, 
                            gridRow: `span 1`,
                            backgroundColor: currentBookBackgroundColor 
                        }}
                        data-book={book.title}
                        data-chapter={chapter}
                        data-cell-number={cellNumber++} // Assign a unique number to the cell
                        onClick={() => handleChapterClick(book.title, chapter)} // Add click handler for chapter
                    >
                        {displaySize.columns <= verseThreshold && (
                            <div className="chapter-number">{chapter}</div>
                        )}
                    </div>
                );
            }
        }
    }

    // Add 50 blank cells after Revelation if zoomIndex <= (maxVerses + 2)
    if (zoomIndex <= (maxVerses + 2)) {
        for (let i = 0; i < 50; i++) {
            booksRendered.push(
                <div
                    key={`blank-cell-${i}`}
                    className="grid-item blank-cell"
                    style={{
                        gridColumn: `span 1`,
                        gridRow: `span 1`,
                        height: Math.max(displaySize.height / Math.ceil((TOTAL_CELLS / maxColumns)))
                    }}
                    data-book="Blank"
                    data-chapter={0}
                    data-cell-number={cellNumber++} // Assign a unique number to the cell
                ></div>
            );
        }
    }

    const handleTitleClick = () => {
        // Remove the 'visible' class from all cells before zooming out
        const container = containerRef.current;
        if (container) {
            const rows = container.querySelectorAll('div[data-book]');
            rows.forEach((row) => row.classList.remove('visible'));
        }
        setZoomIndex(maxColumns + maxVerses); // Zoom out to full grid
    };

    // --- Footer navigation logic ---
    const findChapterAtReferenceLine = (): HTMLElement | null => {
        const container = containerRef.current;
        if (!container) return null;

        const cells = container.querySelectorAll('.grid-item');
        for (const cell of Array.from(cells)) {
            const rect = cell.getBoundingClientRect();
            if (rect.top <= 61 && rect.bottom > 61) {
                const chapter = cell.getAttribute('data-chapter');
                if (chapter && chapter !== "0") {
                    return cell as HTMLElement;
                }
            }
        }
        return null;
    };

    const findAdjacentChapterCell = (currentCell: HTMLElement, direction: 'prev' | 'next'): HTMLElement | null => {
        let sibling = direction === 'prev'
            ? currentCell.previousElementSibling
            : currentCell.nextElementSibling;

        while (sibling) {
            if (
                sibling.classList.contains('grid-item') &&
                sibling.getAttribute('data-chapter') &&
                sibling.getAttribute('data-chapter') !== "0" &&
                sibling.getAttribute('data-book') !== "Blank"
            ) {
                return sibling as HTMLElement;
            }
            sibling = direction === 'prev'
                ? sibling.previousElementSibling
                : sibling.nextElementSibling;
        }
        return null;
    };

    const setTargetAndScroll = (cell: HTMLElement | null) => {
        if (!cell) return;
        const container = containerRef.current;
        if (!container) return;
        container.querySelectorAll('.grid-item.target').forEach(c => c.classList.remove('target'));
        cell.classList.add('target');
        cell.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };

    const handleFooterUp = () => {
        const currentChapter = findChapterAtReferenceLine();
        if (currentChapter) {
            const prevChapter = findAdjacentChapterCell(currentChapter, 'prev');
            setTargetAndScroll(prevChapter);
        }
    };

    const handleFooterDown = () => {
        const currentChapter = findChapterAtReferenceLine();
        if (currentChapter) {
            const nextChapter = findAdjacentChapterCell(currentChapter, 'next');
            setTargetAndScroll(nextChapter);
        }
    };

    const [isBookmarkClearingMode, setIsBookmarkClearingMode] = useState(false);
    const bookmarksContainerRef = useRef<HTMLDivElement>(null);

    const handleClearBookmark = (index: number) => {
        const newBookmarks = [...bookmarks];
        newBookmarks[index] = null;
        setBookmarks(newBookmarks);
        localStorage.setItem('bookmarks', JSON.stringify(newBookmarks));
    };

    return (
        <div className="App">
            <Header
                currentBookTitle={currentBookTitle}
                bookmarks={bookmarks}
                handleBookmarkClick={handleBookmarkClick}
                handleMouseDown={handleMouseDown}
                handleMouseUp={handleMouseUp}
                handleTouchStart={handleTouchStart}
                handleTouchEnd={handleTouchEnd}
                popEffectBookmarkIndex={popEffectBookmarkIndex}
                selectedPalette={selectedPalette}
                paletteColors={paletteColors as PaletteColors} // Cast to the imported type
                handleSearchIconClick={handleSearchIconClick}
                setShowSettings={setShowSettings}
                searchIconRef={searchIconRef}
                settingsIconRef={settingsIconRef}
                handleTitleClick={handleTitleClick} // Pass the new handler
                isBookmarkClearingMode={isBookmarkClearingMode}
                handleClearBookmark={handleClearBookmark}
                bookmarksContainerRef={bookmarksContainerRef}
            />
            {showSearchOverlay && (
                <SearchOverlay
                    showSearchOverlay={showSearchOverlay} // This prop is actually not used by SearchOverlay itself if App controls rendering
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    searchResults={searchResults}
                    handleSearchResultClick={handleSearchResultClick}
                    onClose={() => {
                        setShowSearchOverlay(false);
                        setSearchTerm("");
                        setSearchResults([]);
                    }}
                    searchOverlayRef={searchOverlayRef}
                />
            )}
            {showSettings && (
                <SettingsOverlay
                    onClose={() => setShowSettings(false)}
                    translation={translation}
                    handleTranslationChange={handleTranslationChange}
                    selectedPalette={selectedPalette}
                    handlePaletteChange={handlePaletteChange}
                    bookGroupType={bookGroupType}
                    handleBookGroupTypeChange={handleBookGroupTypeChange}
                    bookTitleCaps={bookTitleCaps}
                    handleBookTitleCapsChange={handleBookTitleCapsChange}
                    bookBorderStyle={bookBorderStyle}
                    handleBookBorderStyleChange={handleBookBorderStyleChange}
                    settingsOverlayRef={settingsOverlayRef}
                    activePage={activeSettingsPage}
                    setActivePage={setActiveSettingsPage}
                    maxColumns={maxColumns}
                    handleMaxColumnsChange={handleMaxColumnsChange}
                    verseThreshold={verseThreshold}
                    handleVerseThresholdChange={handleVerseThresholdChange}
                    maxVerses={maxVerses}
                    handleMaxVersesChange={handleMaxVersesChange}
                    maxFontSize={maxFontSize}
                    handleMaxFontSizeChange={handleMaxFontSizeChange}
                />
            )}
            <div
                ref={containerRef}
                className={`flex-container ${displaySize.columns <= verseThreshold ? 'zoomed-in-view' : ''}`}
                style={{
                    display: 'grid',
                    gridAutoRows: rowHeight, // Apply uniform height or auto height based on verseFactor
                    gridTemplateColumns: `repeat(${displaySize.columns}, ${columnWidth})`,
                    height: `${window.innerHeight - 60 - 40}px`, // Adjust height to account for the fixed header
                    overflowY: 'scroll',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                }}
            >
                {booksRendered}
            </div>
            {/* Down Button */}
            {showDownButton && (
                <button
                    ref={downButtonRef}
                    className="down-button"
                    onClick={() => {
                        if (nextChapterWithVersesRef.current) {
                            nextChapterWithVersesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                        setShowDownButton(false);
                        nextChapterWithVersesRef.current = null;
                    }}
                    aria-label="Scroll to next chapter with verses"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </button>
            )}
            {/* Footer */}
            <div className="app-footer">
                <button
                    aria-label="Previous chapter"
                    className="footer-nav-button"
                    onClick={handleFooterUp}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="18 15 12 9 6 15"></polyline>
                    </svg>
                </button>
                <button
                    aria-label="Next chapter"
                    className="footer-nav-button"
                    onClick={handleFooterDown}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default App;
